import React, { useState } from "react";
import PropTypes from "prop-types";
import { Row, Col } from "react-flexbox-grid/lib/index";

import {
  Card,
  CardActionArea,
  CardHeader,
  CardContent,
  Button,
  Chip,
  Divider,
  LinearProgress,
  DialogContentText,
  Collapse
} from "@material-ui/core";

import Refresh from "@material-ui/icons/Refresh";
import ShareIcon from "@material-ui/icons/Share";
import SimpleTextDialog from "components/SimpleTextDialog";
import SelectSearchable from "components/Select";
import DatePickerWrapped from "components/DatePickerWrapped";
import AdvancedSearchLine from "./AdvancedSearchLine";
import TimeZoneLine from "./TimeZoneLine";
import PopoverDownloadFilters from "./PopoverDownloadFilters";
import DownloadFilters from "./DownloadFilters";

// "sensitivity: base" compares with the base letters,
// ignoring case and accents
const caseInsensitiveLabelSort = (a, b) =>
  a.label.localeCompare(b.label, "en", { sensitivity: "base" });

const handleCopyButtonClick = () => {
  navigator.clipboard.writeText(window.location.href);
};

const FilterCard = props => {
  const [copyText, setCopyText] = useState("Copy Query");
  const apiURL = `https://portal.hakai.org/api${props.lastApiQuery}`;
  // TODO: sort the categories and sort within categories
  const optionsGrouped = Object.keys(props.sampleCategoryTree).map(
    categoryKey => ({
      label: categoryKey,
      options: props.sampleCategoryTree[categoryKey]
        .map(e => ({
          value: e.view_name,
          label: e.display_name
        }))
        .sort(caseInsensitiveLabelSort)
    })
  );

  const handleResetClick = () => {
    props.setMarkerPoints([]);
    props.setShowMap(false);
    props.onClearForm();
    props.setTabValue(0);
  };

  const handleLoadPreviewClick = () => {
    props.actions.fetchData();
    props.setShowMap(true);
    props.onPreviewClick();
  };

  return (
    <Card style={{ overflow: "visible" }}>
      <SimpleTextDialog
        showDialog={props.apiUrlClicked}
        title="API Query URL"
        body={
          <DialogContentText>
            To access this data from your script, use the following URL:
            <textarea
              onFocus={e => e.target.select()}
              cols={85}
              readOnly
              value={decodeURI(apiURL)}
              style={{
                display: "block",
                marginTop: "10px",
                height: "100px"
              }}
            />
          </DialogContentText>
        }
        closeDialog={props.onApiURlClick}
      />

      <CardActionArea
        disableRipple
        disableFocusRipple
        component="a"
        onClick={() => props.actions.setFilterOpen(!props.filterOpen)}
      >
        <CardHeader
          title="Search"
          action={<PopoverDownloadFilters {...props} />}
        />
      </CardActionArea>

      <Collapse in={props.filterOpen}>
        <CardContent>
          <Row top="xs">
            <Col xs={12} md={6}>
              <SelectSearchable
                isGrouped
                isLoading={props.isFetchingSamples}
                placeholder="Sample Type"
                label="Sample Type"
                options={optionsGrouped}
                value={props.sample.view_name}
                onChange={optionSelected =>
                  props.onSampleTypeChange(optionSelected.value)
                }
              />
            </Col>
            {props.columnNames.includes("date") && (
              <React.Fragment>
                <Col xs={3} key="dateFromCol">
                  <DatePickerWrapped
                    floatingLabelText="Date from"
                    disabled={!props.sampleFilter.length}
                    value={props.startDateFilter}
                    onChange={props.onStartDateFilterChange}
                    maxDate={props.endDateFilter}
                  />
                </Col>
                <Col xs={3} key="dateToCol">
                  <DatePickerWrapped
                    floatingLabelText="Date to"
                    disabled={!props.sampleFilter.length}
                    value={props.endDateFilter}
                    onChange={props.onEndDateFilterChange}
                    minDate={props.startDateFilter}
                  />
                </Col>
              </React.Fragment>
            )}
          </Row>
          <DownloadFilters {...props} />

          {props.advancedFiltersValid.length || props.showAdvancedSearch ? (
            <Row around="xs">
              <Col xs={12}>
                <CardHeader
                  title={`Advanced search
                    ${
                      props.advancedFiltersValid.length
                        ? ` - Using ${props.advancedFiltersValid.length} filter(s).`
                        : ""
                    }`}
                />

                <AdvancedSearchLine {...props} />
              </Col>
            </Row>
          ) : (
            ""
          )}

          {props.showTimeZone ? (
            <Row around="xs">
              <Col xs={12}>
                <CardHeader title="Time zone" />

                <TimeZoneLine {...props} />
              </Col>
            </Row>
          ) : (
            ""
          )}
          <Divider style={{ marginTop: "10px" }} />
          {props.sampleFilter ? (
            <Row style={{ marginTop: 20 }}>
              <Col xs={3}>
                <Chip
                  label={
                    props.isFetchingRecordCount ? (
                      <span>
                        Counting records..
                        <div style={{ margin: 10 }}>
                          <LinearProgress />
                        </div>
                      </span>
                    ) : (
                      <span>{props.recordCount} record(s) found. </span>
                    )
                  }
                />
                {props.markerPoints.length > 0 &&
                  props.recordCount &&
                  !props.isFetchingRecordCount && (
                    <Chip
                      style={{ marginLeft: "5px" }}
                      label={
                        <span>
                          {props.markerPoints.length} record(s) on map.{" "}
                        </span>
                      }
                    />
                  )}
              </Col>
              <Col xs={6}>
                <Button
                  variant="contained"
                  color="secondary"
                  style={{ marginLeft: "15px" }}
                  disabled={
                    !props.recordCount ||
                    !props.filterChanged ||
                    props.isFetchingRecordCount
                  }
                  onClick={handleLoadPreviewClick}
                >
                  Load Preview
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    handleCopyButtonClick();
                    setCopyText("Copied URL");
                  }}
                  style={{
                    marginLeft: "15px",
                    color: "#fff"
                  }}
                >
                  {copyText}
                  <ShareIcon />
                </Button>
                <Button
                  style={{ marginLeft: "10px" }}
                  default
                  onClick={handleResetClick}
                >
                  Reset
                  <Refresh />
                </Button>
              </Col>
            </Row>
          ) : null}
        </CardContent>
      </Collapse>
    </Card>
  );
};

FilterCard.propTypes = {
  previewRowCount: PropTypes.number.isRequired,
  onClearForm: PropTypes.func.isRequired,
  onRowCountChange: PropTypes.func.isRequired,
  filterChanged: PropTypes.bool.isRequired,
  showAdvancedSearch: PropTypes.bool.isRequired,
  showTimeZone: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  advancedFiltersValid: PropTypes.arrayOf(PropTypes.object).isRequired,
  isFetchingSamples: PropTypes.bool.isRequired,
  apiUrlClicked: PropTypes.bool.isRequired,
  lastApiQuery: PropTypes.string.isRequired,
  sample: PropTypes.shape({
    display_name: PropTypes.string,
    view_name: PropTypes.string
  }).isRequired,
  onApiURlClick: PropTypes.func.isRequired,
  onSampleSearchClear: PropTypes.func.isRequired,
  sampleIDSearch: PropTypes.arrayOf(PropTypes.string).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  sampleCategoryTree: PropTypes.object.isRequired,
  sampleFilter: PropTypes.string.isRequired,
  columnNames: PropTypes.arrayOf(PropTypes.string).isRequired,

  startDateFilter: PropTypes.oneOfType([
    PropTypes.instanceOf(Date).isRequired,
    PropTypes.string
  ]).isRequired,
  endDateFilter: PropTypes.oneOfType([
    PropTypes.instanceOf(Date).isRequired,
    PropTypes.string
  ]).isRequired,
  setShowMap: PropTypes.func.isRequired,
  setTabValue: PropTypes.func.isRequired,
  setMarkerPoints: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  markerPoints: PropTypes.arrayOf(PropTypes.object).isRequired,
  onPreviewClick: PropTypes.func.isRequired,
  isFetchingRecordCount: PropTypes.bool.isRequired,

  onSampleTypeChange: PropTypes.func.isRequired,
  onStartDateFilterChange: PropTypes.func.isRequired,
  onEndDateFilterChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  preview: PropTypes.arrayOf(PropTypes.object).isRequired,
  recordCount: PropTypes.number.isRequired,
  filterOpen: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    setFilterOpen: PropTypes.func.isRequired,
    fetchData: PropTypes.func.isRequired,
    fetchPreview: PropTypes.func.isRequired
  }).isRequired
};

export default FilterCard;
